import { Modal, Table, message } from "antd"
import dayjs from "dayjs"
import PropTypes from "prop-types"
import React from "react"
import { useState } from "react"
import { FiCopy, FiEdit2, FiTrash } from "react-icons/fi"
import { IoIosLink } from "react-icons/io"
import styled from "styled-components"

import {
    createEvent,
    deleteEvent,
    updateEvent,
    uploadFile,
} from "../../../../../api"
import AppButton from "../../../../_shared/components/AppButton"
import FloatingButton from "../../../../_shared/components/FloatingButton"
import Popconfirm from "../../../../_shared/components/Popconfirm"
import ScreenTitle from "../../../../_shared/components/Typography/ScreenTitle"
import { useStateValue } from "../../../../_shared/context/AppStateStore"
import useRestTable from "../../../../_shared/hooks/usePaginatedRestResource"
import EventLinks from "./EventLinks"
import StyledEventsBottomSheet from "./EventsBottomSheet"

// ----------------------------------------------------------------------------

function AdminEvents(props) {
    // -------------------------------------
    // Props destructuring
    // -------------------------------------

    // -------------------------------------
    // Hooks (e.g. useState, useMemo ...)
    // -------------------------------------

    const [{ token }] = useStateValue()

    const [events, fetchEvents, isLoading, pagination, total] = useRestTable(
        "/events",
        token
    )

    const [selectedEvent, setSelectedEvent] = useState(null)

    // -------------------------------------
    // Effects
    // -------------------------------------

    // -------------------------------------
    // Component functions
    // -------------------------------------

    async function save(values) {
        try {
            const { url } = values.url
                ? typeof values.url === "string"
                    ? { url: values.url }
                    : await uploadFile(values.url, token)
                : { url: "" }
            const { url: detailUrl } = values.detailUrl
                ? typeof values.detailUrl === "string"
                    ? { url: values.detailUrl }
                    : await uploadFile(values.detailUrl, token)
                : { url: "" }
            const body = { ...values, url: url, detailUrl: detailUrl }
            values.id
                ? await updateEvent(values.id, body, token)
                : await createEvent(body, token)
            message.success(
                values?.id ? "Evento aggiornato." : "Evento creato."
            )
            fetchEvents()
            closeSheet()
        } catch (e) {
            message.error(`Non siamo riusciti a creare l'evento: ${e}`)
        }
    }
    function duplicate(row) {
        return () => {
            setSelectedEvent({
                ...row,
                id: null,
                slots: row.slots.map((slot) => ({
                    date: slot.date,
                    window: slot.window,
                    note: "",
                    visible: true,
                    isReservedToAgents: false,
                    locationCode: slot.locationCode,
                    id: null,
                })),
            })
        }
    }

    function closeSheet() {
        setSelectedEvent(null)
    }

    function openEventForm(event) {
        return () => setSelectedEvent(event)
    }

    async function handleDeleteEvent(row) {
        await deleteEvent(row.id, token)
        message.success("Evento eliminato")
        fetchEvents()
    }

    function openEventLinks(event) {
        Modal.info({
            title: "Link utili",
            content: <EventLinks event={event} />,
            width: 600,
        })
    }

    function renderRowActions(row) {
        return (
            <div className="row-actions">
                <FloatingButton onClick={() => openEventLinks(row)}>
                    <IoIosLink className="icn" />
                </FloatingButton>
                <FloatingButton onClick={openEventForm(row)}>
                    <FiEdit2 className="icn" />
                </FloatingButton>
                <FloatingButton onClick={duplicate(row)}>
                    <FiCopy className="icn" />
                </FloatingButton>
                <Popconfirm
                    placement="left"
                    title="Sei sicuro di voler eliminare questo catalogo?"
                    okText="Sì"
                    cancelText="No"
                    onConfirm={() => {
                        handleDeleteEvent(row)
                    }}
                >
                    <FloatingButton>
                        <FiTrash className="icn" />
                    </FloatingButton>
                </Popconfirm>
            </div>
        )
    }

    function handleTableChange(pagination) {
        fetchEvents({ pagination })
    }

    function getPaginationObject() {
        return {
            size: "small",
            total: total,
            current: pagination.current,
            pageSize: pagination.pageSize,
            showSizeChanger: false,
        }
    }

    // -------------------------------------
    // Component local variables
    // -------------------------------------

    const defaultNewEvent = {
        title: "",
        summary: "",
        description: "",
        url: "",
        slots: undefined,
        days: [],
        locations: [],
        city: "",
        province: "",
        address: "",
        price: 0,
        id: null,
    }

    return (
        <div className={props.className}>
            <ScreenTitle text="Eventi"></ScreenTitle>
            <div className="top-bar">
                <AppButton
                    onClick={openEventForm(defaultNewEvent)}
                    uxType="primary"
                    htmlType="button"
                >
                    Crea nuovo evento
                </AppButton>
            </div>
            <div className="container">
                <Table
                    dataSource={events}
                    rowKey={(row) => row.id}
                    columns={getColumns(renderRowActions)}
                    loading={isLoading}
                    pagination={getPaginationObject()}
                    onChange={handleTableChange}
                    checkStrictly={true}
                />
            </div>
            <StyledEventsBottomSheet
                open={selectedEvent ? true : false}
                onConfirm={save}
                onCancel={closeSheet}
                event={selectedEvent}
            />
        </div>
    )
}

// ----------------------------------------------------------------------------
// Component PropTypes and default props
// ----------------------------------------------------------------------------

AdminEvents.propTypes = {
    className: PropTypes.string.isRequired,
}

AdminEvents.defaultProps = {}

// ----------------------------------------------------------------------------

const StyledAdminEvents = styled(AdminEvents)`
    & {
        h1 {
            text-align: left;
        }
        .top-bar {
            margin: 20px 0;

            display: flex;
            flex-direction: row;
            align-items: center;
        }

        .ant-table-cell {
            .row-actions {
                display: flex;
                justify-content: space-between;
            }
        }

        .table {
            tr {
                .row-actions {
                    display: flex;
                    justify-content: space-between;
                    transition: all 0.2s ease-in-out;
                    opacity: 0;
                }

                :hover {
                    .row-actions {
                        opacity: 1;
                    }
                }
            }
        }

        .icn {
            font-size: 20px;
            color: ${(props) => props?.theme?.colors?.textPrimary};
        }
    }
`
// ----------------------------------------------------------------------------

export default StyledAdminEvents

export function getColumns(renderRowActions) {
    return [
        {
            title: "Titolo",
            dataIndex: "title",
            key: "title",
            // eslint-disable-next-line
            render: (text) => (
                <>
                    {text
                        ?.replace("<br>", " ")
                        .replace(/<p>/g, " ")
                        .replace(/<\/p>/g, " ")}
                </>
            ),
        },
        {
            title: "Data",
            dataIndex: "date",
            key: "date",
            // eslint-disable-next-line
            render: (text, record) => (
                <>{dayjs(record?.days[0]?.date).format("DD/MM/YYYY")}</>
            ),
        },
        {
            title: "Location",
            dataIndex: "locations",
            key: "locations",
            render: (locations) => (
                <div>
                    {locations?.map((l) => {
                        const { code, city, province, address } = l
                        return (
                            <div style={{ whiteSpace: "nowrap" }} key={code}>
                                {address}, {city} ({province})
                            </div>
                        )
                    })}
                </div>
            ),
        },
        {
            title: "",
            dataIndex: "",
            key: "range-actions",
            render: renderRowActions,
        },
    ]
}
